import { useMemo } from 'react';
import type { Category } from '@voice/shared';
import { updateCategory } from '../services/api';

interface CategoryGridProps {
  categories: Category[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRefresh?: () => void;
}

const DEFAULT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f59e0b', '#22c55e', '#06b6d4', '#3b82f6',
];

function getCategoryColor(category: Category, index: number): string {
  return category.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

export function CategoryGrid({ categories, selectedId, onSelect, onRefresh }: CategoryGridProps) {
  // Group categories by parent for hierarchy display
  const grouped = useMemo(() => {
    const parentMap = new Map<string, Category[]>();
    const roots: Category[] = [];

    for (const cat of categories) {
      if (cat.parent_id) {
        const siblings = parentMap.get(cat.parent_id) ?? [];
        siblings.push(cat);
        parentMap.set(cat.parent_id, siblings);
      } else {
        roots.push(cat);
      }
    }

    const result: Array<{ category: Category; isChild: boolean; parentName?: string }> = [];
    for (const root of roots) {
      result.push({ category: root, isChild: false });
      const children = parentMap.get(root.id) ?? [];
      for (const child of children) {
        result.push({ category: child, isChild: true, parentName: root.name });
      }
    }
    return result;
  }, [categories]);

  if (categories.length === 0) return null;

  const handleApprove = async (categoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateCategory(categoryId, { approved: true });
      onRefresh?.();
    } catch (err) {
      console.error('Failed to approve category:', err);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>Categories</span>
        {selectedId && (
          <button style={styles.clearBtn} onClick={() => onSelect(null)} type="button">
            Clear
          </button>
        )}
      </div>
      <div style={styles.grid}>
        {grouped.map(({ category: cat, isChild, parentName }, i) => {
          const color = getCategoryColor(cat, i);
          const isActive = selectedId === cat.id;
          const isUnapproved = cat.ai_suggested && !cat.approved;
          const label = isChild && parentName ? `${parentName} / ${cat.name}` : cat.name;

          return (
            <button
              key={cat.id}
              type="button"
              style={{
                ...styles.chip,
                borderColor: isActive ? color : 'var(--color-border)',
                borderStyle: isUnapproved ? 'dashed' : 'solid',
                backgroundColor: isActive ? `${color}20` : 'var(--color-surface)',
                opacity: isUnapproved && !isActive ? 0.7 : 1,
              }}
              onClick={() => onSelect(isActive ? null : cat.id)}
            >
              <span
                style={{
                  ...styles.dot,
                  backgroundColor: color,
                }}
              />
              <span style={{
                ...styles.chipLabel,
                color: isActive ? color : 'var(--color-text-secondary)',
              }}>
                {label}
              </span>
              {isUnapproved && (
                <span
                  style={styles.approveBtn}
                  onClick={(e) => handleApprove(cat.id, e)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleApprove(cat.id, e as unknown as React.MouseEvent); }}
                >
                  &#x2713;
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  clearBtn: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-accent)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'var(--space-2)',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    transition: 'all var(--transition-fast)',
    cursor: 'pointer',
    background: 'none',
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  chipLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
  },
  approveBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-success)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
    lineHeight: 1,
  },
};
